import {
	Dialog,
	Spinner,
	Toolbar
} from "client/Components";
import { useDataInputDialog } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import styles from "./SsoApplicationCreatedDialog.module.less";

export type SsoApplicationCreatedDialogProps = {
	onClose: () => void;
};

export const SsoApplicationCreatedDialog = (props: SsoApplicationCreatedDialogProps): ReactElement => {
	const {
		onClose
	} = props;

	const [remainingTimeInSeconds, setRemainingTimeInSeconds] = useState<number>(60);

	useEffect(
		(): (() => void) => {
			const intervalHandle: SetIntervalHandle = setInterval(
				(): void => {
					setRemainingTimeInSeconds(
						x => Math.max(x - 1, 0)
					);
				},
				1000
			);

			return (): void => clearInterval(intervalHandle);
		},
		[]
	);

	return (
		<Dialog trackingId="sso-application-created-dialog">
			<Dialog.Header title={localize("_Success")} />

			<Dialog.Body>
				<p>{localize("_WeNeedToSignYouOutAndRedirectToAuthenticationType")}</p>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					{remainingTimeInSeconds > 0 && (
						<Toolbar.Custom
							responsiveBehavior="always-visible"
							renderNormalMode={() => (
								<div className={styles.spinner}>
									<Spinner />
								</div>
							)}
							renderCollapsedMode={() => { throw new Error("Does not support collapse"); }}
						/>
					)}
					<Toolbar.Button
						kind="primary"
						isDisabled={remainingTimeInSeconds > 0}
						text={localize("_Ok") + (remainingTimeInSeconds > 0 ? ` (${remainingTimeInSeconds})` : "")}
						trackingId="ok"
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
SsoApplicationCreatedDialog.displayName = "SsoApplicationCreatedDialog";

export function useSsoApplicationCreatedDialog(): [
	element: ReactElement | null,
	open: () => Promise<void>
] {
	const [element, originalOpen] = useDataInputDialog<{}, void>(
		(props, onClose) => (
			<SsoApplicationCreatedDialog
				{...props}
				onClose={onClose}
			/>
		)
	);

	const open = useCallback(
		async (): Promise<void> => {
			await originalOpen({});
		},
		[originalOpen]
	);

	return [element, open];
}
